export const dockerNotes = [
  {
    id: 601,
    topic: "docker",
    seriesKey: "docker",
    title: "Docker Basics",
    description:
      "Defining Docker as a containerization platform, understanding its core benefits, and outlining its main architectural components.",
    content: `
##  What is Docker?
Docker is a **containerization platform** that enables developers to easily package, deploy, and run applications in lightweight, isolated environments called **containers**.
### History & Developer Info
* Founder: Solomon Hykes.
* Year Founded: 2013.
### Why Use Docker?
1. **Consistency ("Works on my machine"):** Docker guarantees the application runs exactly the same way, regardless of the environment (dev, staging, production).
2. **Isolation:** Containers run isolated from each other and the host system, improving security and stability.
3. **Scalability:** Containers can be quickly spun up or shut down to handle varying loads.
##  Docker Architecture
 **1. Docker Engine:** The core technology that creates and runs containers.
**2. Docker Daemon (server):** The background process that manages Docker objects like images, containers, volumes, and networks.
**3. Docker CLI (client):** The command-line interface used to interact with the Docker Daemon.
**4. Docker Images:** Read-only templates used to create containers.
**5. Docker Containers:** Runnable instances of a Docker image.
			`,
    tags: ["Docker", "Basics", "Containerization", "Isolation", "Architecture"],
    date: "2024-11-29",
  },
  {
    id: 602,
    topic: "docker",
    seriesKey: "docker",
    title: "Containers and Images",
    description:
      "Clarifying the relationship between containers and images and mastering the basic Docker commands for managing container lifecycle.",
    content: `
##  Images vs. Containers
 **1.Virtual Machines (VMs):** Each VM includes the application, libraries, and a full guest operating system, making them heavy and slow to start.
**2.Containers:** Package only the application and its dependencies. They share the host system's OS kernel, making them **lightweight, fast, and portable**.
### Docker Images
* A read-only, standardized template for creating containers.
* Built from a set of instructions defined in a Dockerfile.
* Stored in registries like Docker Hub.
### Docker Containers
* A live, runnable instance of an image.
* The isolated environment where the application executes.
##  Container Management Commands
| Command | Purpose | Example |
| :--- | :--- | :--- |
| \`docker pull\`| Downloads an image from a registry (e.g., Docker Hub). | \`docker pull nginx:latest\` |
| \`docker run\` | Creates and starts a container from an image. | \`docker run -d --name webapp nginx\` |
| \`docker stop\` | Gracefully stops a running container. | \`docker stop webapp\` |
| \`docker start\` | Starts a stopped container. | \`docker start webapp\` |
| \`docker rm\` | Removes a stopped container. | \`docker rm webapp\` |
			`,
    tags: [
      "Docker",
      "Containers",
      "Images",
      "VMs",
      "docker run",
      "docker pull",
    ],
    date: "2024-11-29",
  },
  {
    id: 603,
    topic: "docker",
    seriesKey: "docker",
    title: "Docker CLI: Inspection and Execution",
    description:
      "Learning essential Command Line Interface (CLI) commands to monitor, inspect, and interact with running containers.",
    content: `
##  Docker CLI Essentials
The Docker Command Line Interface is how you control the Docker Daemon.
| Command | Purpose | Output |
| :--- | :--- | :--- |
| \`docker ps\` | Lists all running containers. | Container ID, Image, Status, Ports, Names. |
| \`docker ps -a\` | Lists all containers, including stopped ones. | Same as above, plus stopped time. |
| \`docker logs <name/id>\` | Shows the standard output and error streams of a container. | Application logs and startup info. |
| \`docker logs -f <name>\`| Follows the log output in real-time (like \`tail -f\`). | Streaming logs. |
| \`docker exec -it <name> <command>\` | Executes a new command inside a running container. | Shell access, process output. |
| \`docker rmi <image/id>\` | Removes a local image. | Confirmation of image removal. |
### Executing Commands Example
To open an interactive shell (like Bash or Sh) inside a running container named 'db-server':
\`\`\`bash
# -i for interactive, -t for pseudo-TTY
docker exec -it db-server bash
\`\`\`
			`,
    tags: [
      "Docker",
      "CLI",
      "docker ps",
      "docker logs",
      "docker exec",
      "Troubleshooting",
    ],
    date: "2024-11-30",
  },
  {
    id: 604,
    topic: "docker",
    seriesKey: "docker",
    title: "Image Definition and Building",
    description:
      "Understanding the Dockerfile syntax and the process of building custom, version-tagged images from scratch.",
    content: `
## What is a Dockerfile?
A **Dockerfile** is a text file containing all the instructions needed to automatically build a Docker image. Each instruction creates a new read-only layer in the resulting image.
##  Common Dockerfile Instructions
| Instruction | Purpose | Example |
| :--- | :--- | :--- |
| \`FROM\` | Defines the base image (the starting point) for the build. | \`FROM node:18-alpine\` |
| \`WORKDIR\` | Sets the current working directory for subsequent instructions. | \`WORKDIR /app\` |
| \`COPY\`| Copies files from the host machine (build context) into the image. | \`COPY package.json \` |
| \`RUN\` | Executes a command during the image build process (e.g., installing packages). | \`RUN npm install\` |
| \`EXPOSE\` | Documents which ports the application inside the container listens on. | \`EXPOSE 8080\` |
| \`CMD\` | Provides defaults for an executing container. Can be overridden by \`docker run\` command. | \`CMD ["npm", "start"]\` |
| \`ENTRYPOINT\`| Configures a container that will run as an executable. Often used with \`CMD\`. | \`ENTRYPOINT ["/bin/bash"]\` |
##  Building and Tagging Images
The \`docker build\` command reads the Dockerfile in the current directory (\`.\`) and creates an image.
\`\`\`bash
# Build the image and assign a tag/name
# -t : tag (name:version)
docker build -t my-app-image:1.0.0 . 
\`\`\`
			`,
    tags: [
      "Docker",
      "Dockerfile",
      "Image Building",
      "FROM",
      "RUN",
      "CMD",
      "Tagging",
    ],
    date: "2024-11-30",
  },
  {
    id: 605,
    topic: "docker",
    seriesKey: "docker",
    title: "Volumes and Persistent Data Storage",
    description:
      "Implementing Docker Volumes to ensure application data persists even after containers are stopped or deleted.",
    content: `
##  What are Docker Volumes?
Containers are inherently **ephemeral** (data inside is lost when the container is deleted). **Volumes** are the preferred mechanism for persisting data generated or used by Docker containers.
### Types of Storage
 **1.Bind Mounts:** Maps a directory from the **host machine** directly into the container.
* Pros: Easy to use for development (e.g., live code editing).
* Cons: Host path dependent, less portable.
**2.Named Volumes (Preferred):** Docker manages these within a dedicated area on the host file system.
* Pros: Managed by Docker CLI, highly portable, best for databases.
##  Volume Commands
### 1. Bind Mount Syntax (Development)
Used with \`docker run\` to map a host directory to a container path.
\`\`\`bash
# Mounts the current directory ($PWD) to the container's /app directory
docker run -v $PWD:/app my-app-image
\`\`\`
### 2. Named Volume Syntax (Production)
Used with \`docker run\` to create or reference a volume by name.
\`\`\`bash
# Create the volume first (optional, Docker will create it if needed)
docker volume create db-data
# Run the container, mapping the named volume to the container path
docker run -v db-data:/var/lib/mysql mysql:latest
\`\`\`
### 3. Volume Management
* List volumes: \`docker volume ls\`
* Remove volume: \`docker volume rm db-data\`
			`,
    tags: [
      "Docker",
      "Volumes",
      "Bind Mounts",
      "Persistent Storage",
      "docker volume",
    ],
    date: "2024-12-01",
  },
  {
    id: 606,
    topic: "docker",
    seriesKey: "docker",
    title: "Networking and Port Exposure",
    description:
      "Controlling how containers communicate with each other and how they expose their services to the outside host system.",
    content: `
##  Container Networking Basics
By default, Docker creates virtual networks to allow containers to communicate securely.
### Exposing Ports (\`-p\`)
To access an application running inside a container from the host machine or the outside world, you must map the container's internal port to a port on the host machine.
\`\`\`bash
# Syntax: -p hostPort:containerPort
# Access: localhost:8080 connects to the application on port 3000 inside the container
docker run -d -p 8080:3000 my-web-app
\`\`\`
##  Docker Network Drivers
| Driver | Purpose | Use Case |
| :--- | :--- | :--- |
| \`bridge\` | Default. Creates a private network between containers on the same host. Containers can talk to each other by name/IP. | Single-host applications (e.g., web app + database). |
| \`host\` | Removes network isolation. Containers share the host's network stack directly. | Performance-critical or debugging tasks. |
| \`overlay\` | Creates a distributed network that spans multiple Docker hosts. | Docker Swarm or multi-host clusters. |
### Connecting Multiple Containers (Bridge Network)
When running containers on a custom bridge network, they can resolve each other using the container's name (service discovery).
\`\`\`bash
# 1. Create a custom network
docker network create my-app-network
# 2. Run database container on the network
docker run -d --net my-app-network --name db postgres
# 3. Run web container on the network (it can access the DB via "db:5432")
docker run -d --net my-app-network --name webapp my-app
\`\`\`
			`,
    tags: [
      "Docker",
      "Networking",
      "Ports",
      "Bridge",
      "Host",
      "Overlay",
      "Service Discovery",
    ],
    date: "2024-12-01",
  },
  {
    id: 607,
    topic: "docker",
    seriesKey: "docker",
    title: "Managing Multi-container Applications",
    description:
      "Using Docker Compose to define and run multi-container applications with a single configuration file and command.",
    content: `
##  What is Docker Compose?
Docker Compose is a tool for defining and running multi-container Docker applications. It uses a **YAML file** (\`docker-compose.yml\`) to configure all the application's services, networks, and volumes.
### Key Benefits
* Simplicity: Spin up an entire complex stack (web, database, cache) with one command.
* Reproducibility: Ensures all dependencies are consistently configured.
### The \`docker-compose.yml\` Structure
The file defines different **services**, which are equivalent to containers/images.
\`\`\`yaml
version: '3.8'
services
web: # Service 1: The web application
 build: . # Build from Dockerfile in current directory
 ports: 
  - "80:3000"
  volumes:
   - .:/usr/src/app
    depends_on: # Ensures DB starts before the web app  - db
            db: # Service 2: The database  
            image: postgres:15
            environment:
            POSTGRES_USER: user
            POSTGRES_PASSWORD: password
             volumes:
             - postgres_data:/var/lib/postgresql/data

            volumes: # Top-level volume declaration
             postgres_data:
\`\`\`
##  Compose Commands
| Command | Purpose |
| :--- | :--- |
| \`docker compose up\` | Builds, creates, starts, and attaches to containers defined in the YAML file. |
| \`docker compose up -d\` | Runs containers in detached mode (in the background). |
| \`docker compose down\` | Stops containers and removes networks/volumes (unless explicitly told not to). |
| \`docker compose ps\` | Lists all running services in the current project. |
			`,
    tags: ["Docker", "Compose", "YAML", "Multi-container", "docker-compose up"],
    date: "2024-12-02",
  },
  {
    id: 608,
    topic: "docker",
    seriesKey: "docker",
    title: "Environment Variables and Configuration",
    description:
      "Methods for securely passing configuration details and environment variables to containers and services.",
    content: `
##  Passing Configuration to Containers
Configuration (API keys, ports, database URLs) should be injected into the container at runtime, not hardcoded into the image.
### 1. Dockerfile (\`ENV\`)
Sets a default environment variable value during the image build process.
\`\`\`dockerfile
# Sets the default environment
ENV NODE_ENV=production
\`\`\`
### 2. Docker Run (\`-e\`)
Overrides environment variables when the container is run.
\`\`\`bash
# Sets a single environment variable
docker run -e SECRET_KEY=my-secret my-app
\`\`\`
### 3. Docker Compose (\`environment\` / \`.env\`)
The preferred method for multi-container apps.
* \`environment\` block: Lists variables directly in the \`docker-compose.yml\`.
    \`\`\`yaml
    services:
      web:
        environment:
          - API_PORT=3000
          - DB_HOST=db
    \`\`\`
* \`.env\` files (Best Practice): Create a separate \`.env\` file containing sensitive variables. Docker Compose automatically reads this file in the same directory and substitutes the values in the YAML.
    **\`.env\` file:**
    \`\`\`
    DB_PASSWORD=my-secure-password
    \`\`\`

    **\`docker-compose.yml\` fragment:**
    \`\`\`yaml
    services:
      db:
        environment:
          POSTGRES_PASSWORD: \${DB_PASSWORD} # Value is pulled from .env
    \`\`\`
			`,
    tags: [
      "Docker",
      "Configuration",
      "Environment Variables",
      "ENV",
      "e",
      ".env",
    ],
    date: "2024-12-02",
  },
  {
    id: 609,
    topic: "docker",
    seriesKey: "docker",
    title: "Logging and Monitoring Containers",
    description:
      "Methods for viewing container output, understanding logging drivers, and basic resource monitoring.",
    content: `
##  Logging
### 1. Viewing Container Logs
By default, Docker captures everything written to the container's **STDOUT** (standard output) and **STDERR** (standard error) streams.
* View past logs: \`docker logs <name>\`
* Follow logs in real-time: \`docker logs -f <name>\`
* View logs with timestamps: \`docker logs -t <name>\`
### 2. Logging Drivers
Docker allows you to configure a **logging driver** to send container logs to external systems.
* \`json-file\` (Default): Stores logs in JSON files on the local disk.
* \`none\`: Disables logging for the container.
* \`syslog\` / \`gelf\` / \`awslogs\`: Drivers used to forward logs to centralized logging systems (e.g., Splunk, AWS CloudWatch).
##  Monitoring
The **\`docker stats\`** command provides a live stream of resource usage statistics (CPU, Memory, Network I/O) for all running containers.
\`\`\`bash
# Live stream of resource usage
docker stats
# Output columns: CONTAINER ID, CPU %, MEM USAGE / LIMIT, NET I/O, etc.
\`\`\`
			`,
    tags: [
      "Docker",
      "Logging",
      "Monitoring",
      "docker logs",
      "docker stats",
      "Drivers",
    ],
    date: "2024-12-03",
  },
  {
    id: 610,
    topic: "docker",
    seriesKey: "docker",
    title: "Docker Registry and Docker Hub",
    description:
      "Understanding public and private registries and the process of sharing custom images via Docker Hub.",
    content: `
##  Docker Registry
A **Registry** is a storage and distribution service for Docker images.
* Docker Hub: The default, largest public registry. It hosts official images (e.g., \`nginx\`, \`node\`) and user-contributed images.
* Private Registries: Used by organizations to securely store proprietary images (e.g., AWS ECR, Azure Container Registry, GitLab Registry).
##  Pushing Images to Docker Hub
To share a custom-built image, you must tag it with your Docker Hub username and then push it.
### 1. Login
\`\`\`bash
docker login
\`\`\`
### 2. Tag the Image
The tag format must be \`<username>/<image-name>:<tag>\`.
\`\`\`bash
# Assuming your username is "myuser" and image is "my-app-image:1.0.0"
docker tag my-app-image:1.0.0 myuser/my-app:1.0.0
\`\`\`
### 3. Push the Image
This uploads the image to the registry associated with the tag (Docker Hub by default).
\`\`\`bash
docker push myuser/my-app:1.0.0
\`\`\`
##  Pulling Images
When you run \`docker pull <image-name>\`, Docker first checks the local system, then defaults to pulling from Docker Hub.
\`\`\`bash
# Pulls the latest stable version of the official Postgres image
docker pull postgres:latest 
\`\`\`
			`,
    tags: ["Docker", "Registry", "Docker Hub", "Push", "Pull", "Tagging"],
    date: "2024-12-03",
  },
  {
    id: 611,
    topic: "docker",
    seriesKey: "docker",
    title: "Docker Swarm and Orchestration Basics",
    description:
      "An introduction to container orchestration, defining Docker Swarm, and learning basic service deployment and scaling.",
    content: `
### Orchestrating containers
**Container Orchestration** is the automated management of containerized applications, including deployment, scaling, networking, and availability.
### Docker Swarm vs. Kubernetes
* Docker Swarm: Docker's native, simpler orchestration tool. It's integrated into Docker Engine and easier to set up. Ideal for simpler needs and quick clustering.
* Kubernetes (K8s):The industry-standard, complex, and highly feature-rich orchestration platform. Better for large-scale, enterprise-level deployments.
##  Docker Swarm Basics
Swarm turns a pool of Docker hosts into a single virtual server (a Swarm).
### 1. Initialize the Swarm (Manager Node)
The first machine becomes the **Manager** node.
\`\`\`bash
docker swarm init --advertise-addr <THIS_MACHINE_IP>
# Output provides a join token for worker nodes
\`\`\`
### 2. Deploy a Service
A **Service** defines the desired state of a container, including which image to use and how many replicas should run.
\`\`\`bash
# Deploy 5 replicas of the Nginx service on the Swarm
docker service create --name webserver --replicas 5 -p 80:80 nginx:latest
\`\`\`
### 3. Scaling Services
You can dynamically change the number of replicas for a running service.
\`\`\`bash
# Scale the webserver service down to 3 running containers
docker service scale webserver=3
\`\`\`
### 4. Inspection
* View services: \`docker service ls\`
* View tasks (running containers): \`docker service ps webserver\`
			`,
    tags: [
      "Docker",
      "Swarm",
      "Orchestration",
      "Kubernetes",
      "Service",
      "Scaling",
    ],
    date: "2024-12-04",
  },
  {
    id: 612,
    topic: "docker",
    seriesKey: "docker",
    title: "Dockerfile and Resource Management",
    description:
      "Utilizing advanced Dockerfile features like multi-stage builds and setting resource constraints for better performance and efficiency.",
    content: `
##  Advanced Dockerfile Features
### 1. Multi-Stage Builds
This technique uses multiple \`FROM\` statements in a single Dockerfile. You copy only the necessary final artifacts (e.g., a compiled binary or production-ready JavaScript bundle) from an earlier "builder" stage into a final, minimal image.
* Benefit: Dramatically reduces the final image size and attack surface, as development dependencies are discarded.
\`\`\`dockerfile
# Stage 1: Build Environment (large base image)
FROM node:18-alpine AS builder
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
RUN npm run build # Create final production assets

# Stage 2: Production Environment (minimal base image)
FROM node:18-slim
WORKDIR /app
# Copy only the compiled assets from the builder stage
COPY --from=builder /app/build ./build 
CMD ["node", "build/index.js"]
\`\`\`
### 2. Health Checks (\`HEALTHCHECK\`)
Defines a command that Docker can use to periodically check if a running container is still "healthy" (not just running, but functioning correctly).
\`\`\`dockerfile
HEALTHCHECK --interval=5m --timeout=3s \
  CMD curl --fail http://localhost/health || exit 1
\`\`\`
##  Resource Constraints
You can limit the resources a container can consume using \`docker run\` flags:
* \`--memory <limit>\`: Limits the amount of memory (e.g., \`--memory 512m\`).
* \`--cpus <limit>\`: Limits the CPU shares (e.g., \`--cpus 0.5\` for half a CPU core).
##  Cleanup
The \`docker system prune\` command is vital for maintenance, cleaning up unused resources (stopped containers, unused images, dangling volumes, and unused networks).
\`\`\`bash
# Cleans up all dangling (untagged) images, stopped containers, and unused networks
docker system prune -f 
# Cleans up all the above PLUS unused volumes (DANGEROUS if you need the volume data)
docker system prune -a --volumes 
\`\`\`
			`,
    tags: [
      "Docker",
      "Advanced",
      "Multi-stage",
      "HEALTHCHECK",
      "Resources",
      "prune",
    ],
    date: "2024-12-04",
  },
  {
    id: 613,
    topic: "docker",
    seriesKey: "docker",
    title: "Docker in CI/CD and Deployment Pipelines",
    description:
      "Integrating Docker into Continuous Integration and Continuous Delivery workflows and deployment to cloud platforms.",
    content: `
##  Docker in CI/CD
Docker is essential for modern **CI/CD** (Continuous Integration/Continuous Delivery) because it ensures the environment used for testing is identical to the one used for production.
### Pipeline Steps (e.g., GitHub Actions)
 **1.Checkout Code:** Get the source code.
 **2.Build Image:** Run \`docker build -t registry/app:COMMIT_SHA .\` (Tagging with a unique SHA ensures traceability).
**3.Test Image:** Run unit/integration tests *inside* a container.
 **4.Push Image:** \`docker push registry/app:COMMIT_SHA\` (Upload the image to a private registry).
**5.Deployment (CD):** The deployment tool (e.g., Kubernetes, Cloud Run) pulls the newly pushed image and updates the running service.
##  Deployment to Cloud Services
Cloud providers offer services optimized for running containers:
* AWS: ECS(Elastic Container Service) or EKS (Kubernetes on AWS).
* Azure: Azure Container Instances or AKS (Azure Kubernetes Service).
* GCP:Google Kubernetes Engine (GKE) or Cloud Run(serverless containers).
### Docker with Kubernetes
Docker creates the container images, and **Kubernetes** handles the orchestration (scheduling, scaling, and managing) of those containers across a cluster of machines.
			`,
    tags: [
      "Docker",
      "CI/CD",
      "Deployment",
      "GitHub Actions",
      "Kubernetes",
      "AWS",
      "EKS",
    ],
    date: "2024-12-05",
  },
  {
    id: 614,
    topic: "docker",
    seriesKey: "docker",
    title: "Best Practices and Optimization",
    description:
      "A summary of critical guidelines for creating small, efficient, and secure Docker images for production use.",
    content: `
##  Docker Best Practices
### 1. Minimize Image Size
Smaller images are faster to build, pull, and deploy.
* Use Minimal Base Images: Use stripped-down Linux distros like Alpine (\`alpine\`, \`node:18-alpine\`) instead of full distributions (\`ubuntu\`, \`node:18\`).
* Use Multi-Stage Builds: As discussed in 612, discard build-time dependencies and intermediate layers.
* Clean Up: Combine \`RUN\` commands where possible and clean up cached files in the same layer (\`apt-get clean\`, \`rm -rf /var/cache\`).
### 2. Efficient Layering
Docker caches image layers. If an instruction doesn't change, the layer is reused.
* Order Instructions: Place the most stable instructions (e.g., \`FROM\`, \`COPY package.json\`, \`RUN npm install\`) first, and the most frequently changing instructions (e.g., \`COPY app.js\`) last.
### 3. Security
* Use Official Images: Trust images from Docker Hub that are clearly labeled as "Official Images" or verified vendors.
* Least Privilege: Do not run your container as the \`root\` user. Use the \`USER\` instruction in the Dockerfile to switch to a less-privileged user.
### 4. Tagging and Ignoring Files
* Tag Properly: Use clear, descriptive tags (\`app-name:1.0.0\`, \`app-name:latest\`). Avoid using the implicit \`latest\` tag in production.
* Use \`.dockerignore\`: A file similar to \`.gitignore\`. It prevents unnecessary files (e.g., \`node_modules\`, \`.git\`, temporary files) from being sent to the Docker Daemon during the build process, saving time and disk space.
			`,
    tags: [
      "Docker",
      "Best Practices",
      "Optimization",
      "Alpine",
      "Security",
      "dockerignore",
    ],
    date: "2024-12-05",
  },
];
